import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, IconName } from '../components/ui';
import './Landing.css';

const NAV_LINKS = ['Solutions', 'Features', 'Pricing', 'Resources'];
// Only ship products that exist. Sprints/BugTracker/CRM/Creator/QEngine were
// advertised in the nav but have no implementation behind them.
const TOP_PRODUCTS = ['Projects Plus'];
const PRODUCT_TAB_MAP: Record<string, number> = {
  'Projects Plus': 0,
};

const FEATURES: { icon: IconName; title: string; desc: string }[] = [
  {
    icon: 'tasks',
    title: 'Task Management',
    desc: 'Classic list, Kanban board or plain view. Organize and prioritize tasks your way.',
  },
  {
    icon: 'gantt',
    title: 'Gantt Charts',
    desc: 'Visualize project timelines, dependencies and critical paths in one glance.',
  },
  {
    icon: 'clock',
    title: 'Time Tracking',
    desc: 'Log billable and non-billable hours manually or with automatic timers.',
  },
  {
    icon: 'bug',
    title: 'Issue Tracker',
    desc: 'Track bugs and issues with severity, status and custom workflows.',
  },
  {
    icon: 'users',
    title: 'Collaboration',
    desc: 'Chat, forums, documents and real-time feeds keep your team in sync.',
  },
  {
    icon: 'trendingUp',
    title: 'Reports & Dashboards',
    desc: 'Get deep insights into project health, team performance and budgets.',
  },
];

// Usage statistics removed: 500K+ businesses / 10M+ projects / 50M+ tasks / 180+
// countries were invented. Re-add only once we can source real numbers.

// Testimonials removed: these were invented endorsements attributed to named people at
// named companies, which is not something we can publish. Re-add only with real,
// attributable quotes we have permission to use.

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState('Projects Plus');
  const [language, setLanguage] = useState('English');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleNavClick = (link: string) => {
    const sectionMap: Record<string, string> = {
      Solutions: 'features',
      Features: 'features',
      Pricing: 'cta',
      Resources: 'footer',
    };
    const target = sectionMap[link];
    if (target) scrollToSection(target);
  };

  const requestDemo = () => navigate('/register');

  const handleTopProductClick = (product: string) => {
    setSelectedProduct(product);
    setActiveTab(PRODUCT_TAB_MAP[product] ?? 0);
  };

  const cycleLanguage = () => {
    setLanguage(prev => prev === 'English' ? 'हिन्दी' : prev === 'हिन्दी' ? 'ಕನ್ನಡ' : 'English');
  };

  const handleFooterClick = (label: string) => {
    if (label === 'Contact') {
      window.location.href = 'mailto:support@rooman.in?subject=Rooman%20Projects%20Inquiry';
      return;
    }
    if (label === 'Sitemap') {
      scrollToSection('hero');
      return;
    }
    window.location.href = `mailto:support@rooman.in?subject=${encodeURIComponent(`${label} request`)}`;
  };

  return (
    <div className="landing">
      {/* Top utility bar */}
      <div className="top-bar">
        <div className="top-bar-inner">
          <div className="top-bar-products">
            {TOP_PRODUCTS.map((p) => (
              <button key={p} type="button" className="top-bar-product" onClick={() => handleTopProductClick(p)}>{p}</button>
            ))}
          </div>
          <div className="top-bar-actions">
            <button type="button" className="top-bar-icon" onClick={() => scrollToSection('features')} aria-label="Search features"><Icon name="search" size={18} /></button>
            <button type="button" className="top-bar-icon" onClick={cycleLanguage} aria-label="Change language"><Icon name="globe" size={16} /> {language}</button>
            <Link to="/login" className="top-bar-signin">Sign In</Link>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo">
            <div className="logo-icon">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect width="12" height="12" rx="3" fill="#444ce7" />
                <rect x="16" width="12" height="12" rx="3" fill="#f25c2b" />
                <rect y="16" width="12" height="12" rx="3" fill="#3b6df8" />
                <rect x="16" y="16" width="12" height="12" rx="3" fill="#a855f7" />
              </svg>
            </div>
            <div className="logo-text">
              <span className="logo-rooman">Rooman</span>
              <span className="logo-projects">Projects</span>
            </div>
          </Link>

          <div className="navbar-links">
            {NAV_LINKS.map((link) => (
              <button key={link} className="nav-link" onClick={() => handleNavClick(link)}>
                {link}
                {['Solutions', 'Features', 'Resources'].includes(link) && (
                  <span className="nav-arrow">▾</span>
                )}
              </button>
            ))}
          </div>

          <div className="navbar-cta">
            <Link to="/login" className="btn-ghost">Sign In</Link>
            <Link to="/register" className="btn-primary-nav">Request Demo</Link>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span /><span /><span />
          </button>
        </div>

        {/* `mobileMenuOpen` was set here and read nowhere: the button toggled state that
            drove nothing, so below 900px — where the links are hidden — the site had no
            navigation at all, and the dead control was also what pushed the row 39px past
            a 390px viewport. */}
        {mobileMenuOpen && (
          <div className="mobile-menu" id="mobile-menu">
            {NAV_LINKS.map((link) => (
              <button
                key={link}
                className="mobile-menu-link"
                onClick={() => { handleNavClick(link); setMobileMenuOpen(false); }}
              >
                {link}
              </button>
            ))}
            <div className="mobile-menu-actions">
              <Link to="/login" className="btn-ghost" onClick={() => setMobileMenuOpen(false)}>
                Sign In
              </Link>
              <Link
                to="/register"
                className="btn-primary-nav"
                onClick={() => setMobileMenuOpen(false)}
              >
                Request Demo
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero">
        <div className="hero-bg">
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
          <div className="hero-grid" />
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            {selectedProduct}
          </div>

          <h1 className="hero-title">
            Project management
            <br />
            software built for
            <br />
            <span className="hero-title-accent">you</span>
          </h1>

          <div className="hero-checks">
            {['Plan your projects', 'Track work efficiently', 'Collaborate with global teams'].map((c) => (
              <div key={c} className="hero-check">
                <span className="check-icon"><Icon name="check" size={12} strokeWidth={3} /></span>
                {c}
              </div>
            ))}
          </div>

          <div className="hero-actions">
            <Link to="/register" className="btn-cta">
              SIGN UP NOW
            </Link>
            <button type="button" className="btn-demo" onClick={requestDemo}>
              REQUEST DEMO
            </button>
          </div>

          <p className="hero-note">Free forever • No credit card required</p>
        </div>

        {/* Hero UI preview */}
        <div className="hero-preview">
          <div className="preview-card">
            <div className="preview-header">
              <span className="preview-dot red" />
              <span className="preview-dot yellow" />
              <span className="preview-dot green" />
              <span className="preview-title">Active Projects</span>
            </div>
            <div className="preview-table">
              <div className="preview-row preview-row-header">
                <span>ID</span><span>Project Name</span><span>%</span><span>Status</span><span>Tasks</span>
              </div>
              {[
                { id: 'RO-3', name: 'Autonomous-learning', pct: '50%', tasks: 1 },
                { id: 'RO-1', name: 'Compass', pct: '22%', tasks: 50 },
              ].map((p) => (
                <div key={p.id} className="preview-row">
                  <span className="preview-id">{p.id}</span>
                  <span>{p.name}</span>
                  <span>{p.pct}</span>
                  <span className="preview-status">Active</span>
                  <span className="preview-tasks">{p.tasks}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="preview-widget">
            <div className="widget-label">Task progress</div>
            <div className="widget-row">
              <div className="widget-ring">21%</div>
              <div className="widget-info">
                <div className="widget-name">Martin Young</div>
                <div className="widget-date">02/01/2024 to 14/01/2024</div>
                <div className="widget-behind">02 day(s) behind schedule</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features-section">
        <div className="section-header">
          <div className="section-tag">FEATURES</div>
          <h2 className="section-title">Everything your team needs</h2>
          <p className="section-sub">
            From planning to execution, Rooman Projects gives you all the tools<br />
            to deliver projects on time, every time.
          </p>
        </div>

        <div className="features-tabs">
          {['Task Management', 'Gantt Charts', 'Time Tracking', 'Issue Tracker'].map((t, i) => (
            <button
              key={t}
              className={`feature-tab ${activeTab === i ? 'feature-tab-active' : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon"><Icon name={f.icon} size={26} /></div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section id="cta" className="cta-banner">
        <div className="cta-banner-inner">
          <h2 className="cta-title">Start managing projects smarter</h2>
          <p className="cta-sub">Plan, track and deliver your projects in one workspace</p>
          <div className="cta-actions">
            <Link to="/register" className="btn-cta">GET STARTED FREE</Link>
            <button type="button" className="btn-demo" onClick={requestDemo}>SCHEDULE A DEMO</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="footer" className="footer">
        <div className="footer-inner">
          <div className="footer-logo">
            <div className="logo-icon">
              <svg width="22" height="22" viewBox="0 0 28 28" fill="none">
                <rect width="12" height="12" rx="3" fill="#444ce7" />
                <rect x="16" width="12" height="12" rx="3" fill="#f25c2b" />
                <rect y="16" width="12" height="12" rx="3" fill="#3b6df8" />
                <rect x="16" y="16" width="12" height="12" rx="3" fill="#a855f7" />
              </svg>
            </div>
            <span>Rooman Projects</span>
          </div>
          <div className="footer-links">
            {['Privacy', 'Terms', 'Security', 'Sitemap', 'Contact'].map((l) => (
              <button key={l} type="button" className="footer-link" onClick={() => handleFooterClick(l)}>{l}</button>
            ))}
          </div>
          <div className="footer-copy">© 2024 Rooman Technologies Pvt Ltd. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
